// apps/client/src/engine/loop.ts
import { IS_E2E } from '../boot/env';
import { SYSTEM_ORDER, type Frame, type System } from './systems';
// Runtime caps (contract change 36). E2E builds (SwiftShader, a few fps) catch up a whole clamped frame so walking keeps real time.
const STEP_MS = 1000 / 60, MAX_FRAME_MS = 250, MAX_STEPS = IS_E2E ? Math.ceil(MAX_FRAME_MS / STEP_MS) : 5;
const SIM = new Set(SYSTEM_ORDER.slice(0, SYSTEM_ORDER.indexOf('camera')));

export class Loop {
  private readonly sim: System[];
  private readonly view: System[];
  private readonly clock: () => number;
  private last: number | null = null;
  private acc = 0;
  private frameNo = 0;
  private raf = 0;
  private running = false;
  constructor(systems: readonly System[], clock: () => number) {
    const ordered = [...systems].sort((a, b) => SYSTEM_ORDER.indexOf(a.name) - SYSTEM_ORDER.indexOf(b.name));
    this.sim = ordered.filter((s) => SIM.has(s.name));
    this.view = ordered.filter((s) => !SIM.has(s.name));
    this.clock = clock;
  }
  start(): void {
    if (this.running) return;
    this.running = true;
    document.addEventListener('visibilitychange', this.onVisibility);
    const frame = (t: number) => { if (!this.running) return; this.tick(t); this.raf = requestAnimationFrame(frame); };
    this.raf = requestAnimationFrame(frame);
  }
  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.raf);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.last = null;
  }
  /** One rendered frame at rAF time `nowMs`: fixed 60 Hz simulation steps (at most 5), then camera and render once. */
  tick(nowMs: number): void {
    const elapsed = this.last === null ? 0 : Math.min(MAX_FRAME_MS, nowMs - this.last);
    this.last = nowMs;
    this.acc += elapsed;
    const now = this.clock();
    let steps = 0;
    while (this.acc >= STEP_MS && steps < MAX_STEPS) {
      const f: Frame = { now, dt: STEP_MS / 1000, alpha: 0, frameNo: this.frameNo };
      for (const s of this.sim) s.update(f);
      this.acc -= STEP_MS;
      steps++;
    }
    if (steps === MAX_STEPS) this.acc = Math.min(this.acc, STEP_MS);
    const f: Frame = { now, dt: elapsed / 1000, alpha: this.acc / STEP_MS, frameNo: this.frameNo++ };
    for (const s of this.view) s.update(f);
  }
  private readonly onVisibility = () => { if (!document.hidden) this.last = null; };
}
